import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';
import type { CreatorProfile as Creator } from '@/types/portal';
import { api, apiList } from '@/utils/api';
import StepsHeader from './components/StepsHeader';
import BriefForm from './components/BriefForm';
import BriefExtractor from './components/BriefExtractor';
import RecommendationList from './components/RecommendationList';
import ShortlistReview from './components/ShortlistReview';
import ContractStep from './components/ContractStep';
import InviteStep from './components/InviteStep';
import { emptyBrief, briefExample, emptyContractDraft, type CampaignBrief, type CampaignAttachment, type ContractDraft } from './types';
import { matchableContentTypes, creatorCustomAffinity, basedInSelectedMarkets, coversSelectedMarkets, creatorHomeMarket, customFormatAffinity } from './helpers';
import { convertCurrency } from '@/utils/currency';

const defaultMessage =
  'Hi {{creator}}, we are inviting you to {{campaign}} for {{brand}}. {{description}}\n\nWhat we need from you:\n{{deliverables}}\n\nThis is a paid collaboration across {{contentTypes}} with a proposed fee of {{rate}}. The full brief is attached — let us know if you are available and we will send the contract for e-signature.';

export default function NewCampaign() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [brief, setBrief] = useState<CampaignBrief>(emptyBrief);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rates, setRates] = useState<Record<string, number>>({});
  const [message, setMessage] = useState(defaultMessage);
  const [contract, setContract] = useState<ContractDraft>(emptyContractDraft);
  const [attachments, setAttachments] = useState<CampaignAttachment[]>([]);
  const [sent, setSent] = useState(false);
  const [includeOtherMarkets, setIncludeOtherMarkets] = useState(false);
  const [creators, setCreators] = useState<Creator[]>([]);
  const [creatorsError, setCreatorsError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [campaignId, setCampaignId] = useState('');
  // How many shortlisted creators the API actually invited (some may be skipped).
  const [invitedCount, setInvitedCount] = useState(0);

  // Creators with a Twiva wallet, as Laravel's create page offers. Their formats
  // are stored as free text ("Reels, Short video"), so map them onto the brief's
  // content-type ids with the same keywords as a custom format.
  useEffect(() => {
    apiList<Creator>('/discovery?sort=followers')
      .then(({ data }) =>
        setCreators(
          data.map((creator) => ({
            ...creator,
            contentFormats: [...new Set(creator.contentFormats.flatMap((format) => customFormatAffinity(format)))],
          })),
        ),
      )
      .catch((error: Error) => setCreatorsError(error.message));
  }, []);

  const { recommendations, overBudget } = useMemo(() => {
    const matchable = matchableContentTypes(brief.contentTypes);
    const customText = brief.customContentType.trim();
    const markets = brief.markets;
    // No fit score in production yet, so custom-format affinity and followers rank.
    const rankScore = (creator: Creator) =>
      (creator.fitScore ?? 0) + creatorCustomAffinity(creator, customText).length * 5;

    // Does the creator fit the market + content-type brief (budget aside)?
    const fitsBrief = (creator: Creator) => {
      // A creator with no location or formats on record (the agency API sends none)
      // is unknown, not a mismatch: keep them, ranked by followers.
      const knowsMarket = creatorHomeMarket(creator) !== '' || creator.markets.length > 0;
      // Markets: by default only creators actually based in a selected market.
      if (knowsMarket && !basedInSelectedMarkets(creator, markets)) {
        if (!includeOtherMarkets || !coversSelectedMarkets(creator, markets)) {
          return false;
        }
      }
      if (
        matchable.length &&
        creator.contentFormats.length > 0 &&
        !matchable.some((id) => creator.contentFormats.includes(id))
      ) {
        return false;
      }
      return true;
    };

    // A single creator's fee must not exceed the overall campaign budget.
    // Rates and budget may sit in different currencies, so normalise first.
    const withinBudget = (creator: Creator) => {
      if (brief.budget <= 0 || creator.rate == null) return true;
      const price = convertCurrency(creator.rate, creator.currency, brief.currency);
      if (price === null) return true; // unknown FX — never hide on a guess
      return price <= brief.budget;
    };

    const fitsAll = creators.filter(fitsBrief);
    const excludedByBudget = fitsAll.filter((creator) => !withinBudget(creator)).length;
    const list = fitsAll
      .filter(withinBudget)
      .sort((a, b) => {
        const homeDelta =
          Number(basedInSelectedMarkets(b, markets)) -
          Number(basedInSelectedMarkets(a, markets));
        if (homeDelta !== 0) return homeDelta;
        return rankScore(b) - rankScore(a) || b.followers - a.followers;
      });

    return { recommendations: list, overBudget: excludedByBudget };
  }, [
    creators,
    brief.markets,
    brief.contentTypes,
    brief.customContentType,
    brief.budget,
    brief.currency,
    includeOtherMarkets,
  ]);

  const selectedCreators = useMemo(
    () => creators.filter((creator) => selectedIds.includes(creator.id)),
    [creators, selectedIds],
  );

  const generate = () => {
    setSelectedIds(recommendations.slice(0, 6).map((creator) => creator.id));
    setStep(2);
  };

  const toggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
  };

  const setRate = (id: string, value: number) => {
    setRates((prev) => ({ ...prev, [id]: value }));
  };

  /** CampaignController::store — the campaign starts in Recruiting with the shortlist invited. */
  const send = async () => {
    setSending(true);
    setSendError('');
    try {
      const created = await api<{ id: string; invited?: number }>('/campaigns', {
        method: 'POST',
        body: JSON.stringify({ brief, creatorIds: selectedIds, contractTerms: contract.body, inviteMessage: message }),
      });
      setCampaignId(created.id);
      setInvitedCount(created.invited ?? selectedIds.length);
      setSent(true);
    } catch (error) {
      setSendError(error instanceof Error ? error.message : 'Could not create the campaign.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <p className="text-xs font-label font-medium uppercase tracking-wider text-foreground-400">
            Campaigns · New
          </p>
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            {brief.name || 'New campaign'}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            Build a brief, shortlist creators and send invites.
          </p>
        </div>
        <Button variant="ghost" icon="ri-close-line" onClick={() => navigate('/campaigns')}>
          Cancel
        </Button>
      </div>

      <div className="mt-6">
        <StepsHeader current={sent ? 5 : step} onJump={(target) => setStep(target)} />
      </div>

      <div className="mt-6">
        {step === 1 ? (
          <>
          <BriefExtractor
            onApply={(patch, terms) => {
              setBrief((prev) => ({ ...prev, ...patch }));
              if (terms) setContract((prev) => ({ ...prev, body: terms }));
            }}
          />
          <BriefForm
            brief={brief}
            onChange={(patch) => setBrief((prev) => ({ ...prev, ...patch }))}
            onGenerate={generate}
            onUseExample={() => setBrief(briefExample)}
            attachments={attachments}
            onAttachmentsChange={setAttachments}
          />
          </>
        ) : null}

        {step === 2 ? (
          <>
          {creatorsError ? (
            <div role="alert" className="mb-4 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-800">
              <i className="ri-error-warning-line mr-1" />
              Couldn't load creators: {creatorsError}
            </div>
          ) : null}
          <RecommendationList
            creators={recommendations}
            markets={brief.markets}
            includeOtherMarkets={includeOtherMarkets}
            onIncludeOtherMarketsChange={setIncludeOtherMarkets}
            contentTypes={brief.contentTypes}
            customContentType={brief.customContentType}
            inviteOnly={brief.inviteOnly}
            budget={brief.budget}
            currency={brief.currency}
            overBudget={overBudget}
            selectedIds={selectedIds}
            onToggle={toggle}
            onSelectTop={() => setSelectedIds(recommendations.slice(0, 6).map((c) => c.id))}
            onSetSelected={(ids, selected) =>
              setSelectedIds((prev) =>
                selected ? [...new Set([...prev, ...ids])] : prev.filter((value) => !ids.includes(value)),
              )
            }
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
          />
          </>
        ) : null}

        {step === 3 ? (
          <ShortlistReview
            creators={selectedCreators}
            contentTypes={brief.contentTypes}
            rates={rates}
            budget={brief.budget}
            currency={brief.currency}
            onRateChange={setRate}
            onRemove={(id) => setSelectedIds((prev) => prev.filter((value) => value !== id))}
            onBack={() => setStep(2)}
            onNext={() => setStep(4)}
          />
        ) : null}

        {step === 4 ? (
          <ContractStep
            draft={contract}
            onDraft={(patch) => setContract((prev) => ({ ...prev, ...patch }))}
            creators={selectedCreators}
            contentTypes={brief.contentTypes}
            customContentType={brief.customContentType}
            rates={rates}
            brand={brief.brandClient}
            campaignName={brief.name}
            deliverables={brief.deliverables}
            currency={brief.currency}
            startDate={brief.startDate}
            endDate={brief.endDate}
            onBack={() => setStep(3)}
            onNext={() => setStep(5)}
          />
        ) : null}

        {step === 5 ? (
          <InviteStep
            brief={brief}
            creators={selectedCreators}
            rates={rates}
            customContentType={brief.customContentType}
            message={message}
            onMessage={setMessage}
            contract={contract}
            attachments={attachments}
            onBack={() => (sent ? navigate(`/campaigns/${campaignId}`) : setStep(4))}
            onSend={() => void send()}
            sending={sending}
            error={sendError}
            sent={sent}
            invitedCount={invitedCount}
          />
        ) : null}
      </div>

    </div>
  );
}